/**
 * Start work from anywhere (slice 3.2): the rules the "Process" buttons
 * follow, pure (the Processing tab's StartWork.jsx and the job card's
 * Capture tab share them; startWorkModel.test.js). The server
 * (functions handlers/processing/startWork.ts) decides; these only say it.
 */

/** "on: automatic" in the picker: the scheduler's choice, the best-suited free computer. */
export const AUTOMATIC = "";

/** A computer's state in a few words: what it is doing now. */
export function machineWord(m) {
  if (!m.online) return "offline";
  return m.busy ? "busy" : "idle";
}

/**
 * The "on" picker: automatic first, then every computer the server offers
 * (it can run the work, on this job). A computer it does not offer is never
 * in the picker; it is listed under it with why, so nobody wonders where P3 went.
 */
export function pickerOf(options) {
  const machines = Array.isArray(options?.machines) ? options.machines : [];
  return {
    choices: [
      { value: AUTOMATIC, label: "Automatic (the best free computer)" },
      ...machines.filter((m) => m.offered).map((m) => ({ value: m.uid, label: `${m.label} · ${machineWord(m)}` })),
    ],
    notOffered: machines.filter((m) => !m.offered).map((m) => ({ uid: m.uid, label: m.label, why: m.why || "it cannot run this" })),
  };
}

/** The picker's value, or automatic when that computer is no longer offered. */
export function validTarget(options, target) {
  if (!target) return AUTOMATIC;
  return (options?.machines ?? []).some((m) => m.uid === target && m.offered) ? target : AUTOMATIC;
}

export const labelIn = (options) => (uid) => (options?.machines ?? []).find((m) => m.uid === uid)?.label || "the chosen computer";

/** One capture's button. */
export const processLabel = (target, labelOf) => (target ? `Process on ${labelOf(target)}` : "Process next");

/** A request id for one press (the server does a press once, however often the network repeats it). */
export function newRequestId() {
  const rnd = typeof crypto !== "undefined" && crypto.getRandomValues
    ? Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(16).padStart(2, "0")).join("")
    : Math.random().toString(16).slice(2) + Date.now().toString(16);
  return `tap-${rnd}`.slice(0, 64);
}

/**
 * Whether a press's answer is unknown (the call timed out or the network
 * dropped, or the server says that press is still running): its request id
 * is then kept, so pressing again asks about the same press (the server
 * answers it from its row, never doing it twice) instead of starting a new one.
 */
export function answerUnknown(e) {
  const code = String(e?.code || "");
  return /deadline-exceeded|unavailable|internal|unknown|cancelled/.test(code) || e?.details?.reason === "in_progress";
}

/**
 * The request ids of presses whose answer is unknown, by what was pressed
 * (a capture or the job, and the computer): `idFor` gives the kept one or a
 * new one, `settle` forgets it once an answer came.
 */
export function requestIds() {
  const kept = new Map();
  return {
    // Kept from the moment it is sent, so a screen closed while it is on its way still knows it.
    idFor: (key) => {
      const id = kept.get(key) ?? newRequestId();
      kept.set(key, id);
      return id;
    },
    // An unknown answer leaves the registry as it is (idFor kept the id; a newer press's id is never
    // put back over); an answer forgets the id, only if it is still the one kept.
    settle: (key, id, e = null) => {
      if (e && answerUnknown(e)) return;
      if (kept.get(key) === id) kept.delete(key);
    },
  };
}

/**
 * One registry for the whole page, outliving the screens: the Processing
 * tab and the job card's sheet use it with keys naming the job, the capture
 * (or the whole job) and the computer, so closing a screen and pressing
 * again later still asks about the same press.
 */
export const pressIds = requestIds();
export const pressKey = (jobId, sn, target) => `${jobId}|${sn || "job"}|${target || "auto"}`;

const PAUSED =" Processing is paused: it starts when an admin resumes it.";

/** What one capture's press did, in words. */
export function captureResultText(r, labelOf) {
  const o = r?.outcome ?? {};
  const made = r?.created ? "Its photos joined the queue. " : "";
  const paused = r?.paused ? PAUSED : "";
  if (o.result === "assigned") return `${made}${labelOf(o.machineUid)} takes it next (it is held for that computer for 10 minutes).${paused}`;
  if (o.result === "bumped") return `${made}Moved to the front: the best free computer takes it next.${paused}`;
  if (o.result === "already" && o.state === "claimed") return `Already being processed${o.machineUid ? ` on ${labelOf(o.machineUid)}` : ""}.`;
  if (o.result === "already") return `${o.machineUid ? `Already set to run on ${labelOf(o.machineUid)}.` : "Already at the front of the queue."}${paused}`;
  return "Done.";
}

/** What a job's press did, in words. */
export function jobResultText(r, labelOf) {
  const parts = [];
  if (r?.created) parts.push(`${r.created} ${r.created === 1 ? "capture" : "captures"} joined the queue`);
  if (r?.machineUid) {
    parts.push(`${r.assigned} set to run on ${labelOf(r.machineUid)}${r.already ? ` (${r.already} already were)` : ""}`);
  } else if (!r?.created && !r?.missing && r?.scanComplete !== false) {
    // Said only when the press read the whole job and found nothing to make.
    parts.push("every verified capture is already in the queue");
  }
  const { busy = 0, ...others } = r?.refused ?? {};
  const refused = Object.values(others).reduce((n, v) => n + v, 0);
  if (refused) parts.push(`${refused} could not be started`);
  if (busy) parts.push("another press is holding this job's captures for that computer right now: try again in a minute");
  if (r?.missing) parts.push(`${r.missing} more join within 10 minutes`);
  if (r?.notReached) parts.push(`${r.notReached} more stay in the queue for any free computer`);
  if (r?.scanComplete === false) parts.push("not every capture of the job was read in time: press again");
  const text = parts.length ? `${parts.join("; ")}.` : "Nothing to start.";
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}${r?.paused ? PAUSED : ""}`;
}

const STATE_WORDS = {
  queued: "In the queue", claimed: "Being processed", done: "Model made", held: "Waiting", needs_staff: "Needs an admin",
  failed: "Failed", cancelled: "Cancelled",
};

/** One capture's place in the queue, in words (the job card's sheet). */
export function captureStateLine(capture, labelOf) {
  if (!capture) return "No capture with this SN on this job.";
  if (!capture.state) return capture.uploadVerified ? "Its photos are in the cloud; not in the queue yet." : "Its photos are not in the cloud yet.";
  const word = STATE_WORDS[capture.state] || capture.state;
  if (capture.state === "claimed") return `${word}${capture.machineUid ? ` on ${labelOf(capture.machineUid)}` : ""}.`;
  if (capture.state === "queued" && capture.assigned) {
    return capture.assigned.by === "person" ? `${word}, set to run on ${labelOf(capture.assigned.machineUid)}.` : `${word}, next for ${labelOf(capture.assigned.machineUid)}.`;
  }
  if (capture.state === "queued") return capture.priority === 0 ? `${word}, at the front.` : `${word}.`;
  return capture.reason ? `${word}: ${capture.reason}` : `${word}.`;
}

/** Whether a press can do anything for this capture (else the sheet says why and offers no button). */
export function canStartCapture(capture) {
  if (!capture) return false;
  if (!capture.state) return capture.uploadVerified === true;
  return capture.state === "queued";
}
